import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDiscoveryClient } from '../api/context';
import type {
  RepeatRunsResponse,
  RunAddedResponse,
  ScheduleAddedResponse,
  ScheduleRunsResponse,
  ScrapeParams,
  SearchSchedule,
} from '../api/types';

const key = (folderPublicId: string) => ['discovery', 'schedules', folderPublicId];
/** The cross-folder Repeat runs page and its run history. */
const repeatRunsKey = ['discovery', 'repeat-runs'] as const;

export function useSchedules(folderPublicId: string) {
  const client = useDiscoveryClient();
  return useQuery<SearchSchedule[]>({
    queryKey: key(folderPublicId),
    queryFn: () => client.schedules.list(folderPublicId),
    enabled: !!folderPublicId,
  });
}

export function useCreateSchedule(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; everyDays: number; params: ScrapeParams }) =>
      client.schedules.create(folderPublicId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(folderPublicId) });
      void qc.invalidateQueries({ queryKey: repeatRunsKey });
    },
  });
}

export function useUpdateSchedule(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { publicId: string; patch: { enabled?: boolean; everyDays?: number } }) =>
      client.schedules.update(v.publicId, v.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(folderPublicId) });
      void qc.invalidateQueries({ queryKey: repeatRunsKey });
    },
  });
}

export function useDeleteSchedule(folderPublicId: string) {
  const client = useDiscoveryClient();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => client.schedules.remove(publicId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(folderPublicId) });
      void qc.invalidateQueries({ queryKey: repeatRunsKey });
    },
  });
}

/** Every schedule in the team, across folders. */
export function useAllSchedules() {
  const client = useDiscoveryClient();
  return useQuery<RepeatRunsResponse>({
    queryKey: [...repeatRunsKey, 'list'],
    queryFn: () => client.schedules.listAll(),
    // A 404 means the server predates this page; retrying won't change that.
    retry: false,
  });
}

export function useScheduleRuns(publicId: string | null, page: number) {
  const client = useDiscoveryClient();
  return useQuery<ScheduleRunsResponse>({
    queryKey: [...repeatRunsKey, 'runs', publicId, page],
    queryFn: () => client.schedules.runs(publicId!, page),
    enabled: !!publicId,
    retry: false,
  });
}

/** Every creator a schedule's runs added — the list under an expanded Repeat runs row. */
export function useScheduleAdded(publicId: string | null, page: number) {
  const client = useDiscoveryClient();
  return useQuery<ScheduleAddedResponse>({
    queryKey: [...repeatRunsKey, 'schedule-added', publicId, page],
    queryFn: () => client.schedules.added(publicId!, page),
    enabled: !!publicId,
    retry: false,
  });
}

export function useRunAdded(jobId: string | null, page: number) {
  const client = useDiscoveryClient();
  return useQuery<RunAddedResponse>({
    queryKey: [...repeatRunsKey, 'added', jobId, page],
    queryFn: () => client.jobs.added(jobId!, page),
    enabled: !!jobId,
    retry: false,
  });
}
